import {IFunctionContainer} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/ClientFunction';
import {Function, Param} from '@mintdata/mint-kit/api/decorators/Function';
import {FunctionFeature, ISheetAPI, ISheetLocation, SheetType} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {ANumber} from '@mintdata/mint-kit/api/values/ANumber';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {EvalErrors} from '@mintdata/mint-kit/api/values/errors/EvalErrors';
import {AFrame} from '@mintdata/mint-kit/api/values/frames/AFrame';
import {IFrame, IFrameRecord} from '@mintdata/mint-kit/api/values/frames/Frame';

export class SurfaceFunctions implements IFunctionContainer
{
    public get namespace()
    {
        return 'surface';
    }

    @Function({
        title: 'GOTO_PAGE(pageName)',
        alias: 'GOTO_PAGE',
        lazyOnly: true,
        tip: 'Change page on the surface',
        returns:
            {
                title: 'go to page',
                tip: 'Change page on the surface',
                type: Type.Boolean
            }
    })
    public gotoPage(@Param({
                        title: 'sheetAPI',
                        tip: 'API',
                        type: SheetType.SheetAPI
                    }) api:ISheetAPI,
                    @Param({
                        title: 'pageName',
                        tip: 'Name of the page that will be shown',
                        type: Type.String
                    }) pageName:AString,
                    @Param({
                        title: 'isAnimated',
                        tip: 'Use transition animation when new page appears. Default: TRUE',
                        optional: true,
                        type: Type.Boolean
                    }) isAnimated:ABoolean):ABoolean
    {
        const isAnimatedValue = isAnimated ? isAnimated.value : true;
        const successfullyEnded:boolean = api.getSurfaceContext().setActivePage(pageName.value, isAnimatedValue);

        return ABoolean.fromBoolean(successfullyEnded);
    }

    @Function({
        title: 'CURRENT_PAGE()',
        alias: 'CURRENT_PAGE',
        lazyOnly: true,
        tip: 'Returns the name of a current page',
        returns:
            {
                title: 'current page',
                tip: 'Returns the name of a current page',
                type: Type.String
            }
    })
    public currentPage(@Param({
        title: 'sheetAPI',
        tip: 'API',
        type: SheetType.SheetAPI
    })
                           api:ISheetAPI):AString
    {
        const currentPageName:string = api.getSurfaceContext().getActivePageName();

        return AString.fromString(currentPageName);
    }

    @Function({
        title: 'SHOW_OVERLAY(pageName)',
        alias: 'SHOW_OVERLAY',
        lazyOnly: true,
        tip: 'Show a given page above the active surface page as overlay',
        returns:
            {
                title: 'show overlay',
                tip: 'Show a given page above the active surface page as overlay',
                type: Type.Boolean
            }
    })
    public showOverlay(@Param({
                           title: 'sheetAPI',
                           tip: 'API',
                           type: SheetType.SheetAPI
                       }) api:ISheetAPI,
                       @Param({
                           title: 'pageName',
                           tip: 'Name of the page that will be shown',
                           type: Type.String
                       }) pageName:AString,
                       @Param({
                           title: 'isAnimated',
                           tip: 'Use transition animation when the overlay appears. Default: TRUE',
                           optional: true,
                           type: Type.Boolean
                       }) isAnimated:ABoolean):ABoolean
    {
        const isAnimatedValue = isAnimated ? isAnimated.value : true;
        const successfullyEnded:boolean = api.getSurfaceContext().showOverlay(pageName.value, isAnimatedValue);

        return ABoolean.fromBoolean(successfullyEnded);
    }

    @Function({
        title: 'HIDE_OVERLAY(pageName, hideAllAbove)',
        alias: 'HIDE_OVERLAY',
        lazyOnly: true,
        tip: 'Hide overlay',
        returns:
            {
                title: 'Hide overlay',
                tip: 'Hide overlay',
                type: Type.Boolean
            }
    })
    public hideOverlay(@Param({
                           title: 'sheetAPI',
                           tip: 'API',
                           type: SheetType.SheetAPI
                       }) api:ISheetAPI,
                       @Param({
                           title: 'pageName',
                           tip: 'Name of the page that will be hidden',
                           type: Type.String
                       }) pageName:AString,
                       @Param({
                           title: 'hideAllAbove',
                           tip: 'Hide the overlay and all overlays above. Default: TRUE',
                           optional: true,
                           type: Type.Boolean
                       }) hideAllAbove:ABoolean,
                       @Param({
                           title: 'isAnimated',
                           tip: 'Use transition animation when the overlay disappears. Default: TRUE',
                           optional: true,
                           type: Type.Boolean
                       }) isAnimated:ABoolean):ABoolean
    {
        const hideAllAboveValue = hideAllAbove ? hideAllAbove.value : true;
        const isAnimatedValue = isAnimated ? isAnimated.value : true;
        const successfullyEnded:boolean = api.getSurfaceContext()
            .hideOverlay(pageName.value, hideAllAboveValue, isAnimatedValue);

        return ABoolean.fromBoolean(successfullyEnded);
    }

    @Function({
        title: 'HIDE_TOPMOST_OVERLAY()',
        alias: 'HIDE_TOPMOST_OVERLAY',
        lazyOnly: true,
        tip: 'Hide topmost overlay',
        returns:
            {
                title: 'Hide topmost overlay',
                tip: 'Hide topmost overlay',
                type: Type.Boolean
            }
    })
    public hideTopmostOverlay(@Param({
                                  title: 'sheetAPI',
                                  tip: 'API',
                                  type: SheetType.SheetAPI
                              }) api:ISheetAPI,
                              @Param({
                                  title: 'isAnimated',
                                  tip: 'Use transition animation when the overlay disappears. Default: TRUE',
                                  optional: true,
                                  type: Type.Boolean
                              }) isAnimated:ABoolean):ABoolean
    {
        const isAnimatedValue = isAnimated ? isAnimated.value : true;
        const successfullyEnded:boolean = api.getSurfaceContext().hideTopmostOverlay(isAnimatedValue);

        return ABoolean.fromBoolean(successfullyEnded);
    }

    @Function({
        title: 'HIDE_ALL_OVERLAYS()',
        alias: 'HIDE_ALL_OVERLAYS',
        lazyOnly: true,
        tip: 'Hide all overlays',
        returns:
            {
                title: 'Hide all overlays',
                tip: 'Hide all overlays',
                type: Type.Boolean
            }
    })
    public hideAllOverlays(@Param({
                               title: 'sheetAPI',
                               tip: 'API',
                               type: SheetType.SheetAPI
                           }) api:ISheetAPI,
                           @Param({
                               title: 'isAnimated',
                               tip: 'Use transition animation when the all overlays disappears. Default: TRUE',
                               optional: true,
                               type: Type.Boolean
                           }) isAnimated:ABoolean):ABoolean
    {
        const isAnimatedValue = isAnimated ? isAnimated.value : true;
        const successfullyEnded:boolean = api.getSurfaceContext().hideAllOverlays(isAnimatedValue);

        return ABoolean.fromBoolean(successfullyEnded);
    }

    @Function({
        title: 'TOGGLE_OVERLAY(pageName, hideAllAbove)',
        alias: 'TOGGLE_OVERLAY',
        lazyOnly: true,
        tip: 'Toggle showing the given page as overlay',
        returns:
            {
                title: 'Toggle overlay',
                tip: 'Toggle showing the given page as overlay',
                type: Type.Boolean
            }
    })
    public toggleOverlay(@Param({
                             title: 'sheetAPI',
                             tip: 'API',
                             type: SheetType.SheetAPI
                         })
                             api:ISheetAPI,
                         @Param({
                             title: 'pageName',
                             tip: 'Name of the page that will be toggled',
                             type: Type.String
                         }) pageName:AString,
                         @Param({
                             title: 'hideAllAbove',
                             tip: 'Toggle the overlay and all overlays above. Default: TRUE',
                             optional: true,
                             type: Type.Boolean
                         }) hideAllAbove:ABoolean,
                         @Param({
                             title: 'isAnimated',
                             tip: 'Use transition animation when the overlay toggled. Default: TRUE',
                             optional: true,
                             type: Type.Boolean
                         }) isAnimated:ABoolean):ABoolean
    {
        const hideAllAboveValue = hideAllAbove ? hideAllAbove.value : true;
        const isAnimatedValue = isAnimated ? isAnimated.value : true;
        const successfullyEnded:boolean = api.getSurfaceContext()
            .toggleOverlay(pageName.value, hideAllAboveValue, isAnimatedValue);

        return ABoolean.fromBoolean(successfullyEnded);
    }

    @Function({
        title: 'VIEWPORT()',
        tip: 'Returns the viewport information (width, height, type)',
        alias: 'VIEWPORT',
        usesFeatures: [FunctionFeature.ModifiesSpreadsheet],
        returns:
            {
                type: Type.Frame,
                title: 'viewport',
                tip: 'Viewport info as a frame'
            }
    })
    public viewport(@Param({
        title: 'sheetAPI',
        tip: 'API',
        type: SheetType.SheetAPI
    }) api:ISheetAPI):IFrame | AError
    {
        return api.getSheetContext().getViewportInfoFrame() || EvalErrors.Error;
    }

    @Function({
        title: 'ADD_COMPONENT(pageName, parentComponentName, componentType, componentName, xOffsetInParent, yOffsetInParent, width, height)',
        alias: 'ADD_COMPONENT',
        lazyOnly: true,
        tip: 'Adds a new component to the page',
        returns: {
            title: 'boolean',
            tip: 'Returns TRUE if a component has been added to the page. Otherwise returns FALSE.',
            type: Type.Boolean
        }
    })
    public async addComponent(@Param({
                                  title: 'sheetAPI',
                                  tip: 'API',
                                  type: SheetType.SheetAPI
                              })api:ISheetAPI,
                              @Param({
                                  title: 'pageName',
                                  tip: 'Name of the page',
                                  type: Type.String
                              }) pageName:AString,
                              @Param({
                                  title: 'parentComponentName',
                                  tip: 'Name of the parent component',
                                  type: Type.String,
                                  optional: true
                              }) parentComponentName:AString,
                              @Param({
                                  title: 'componentType',
                                  tip: 'Type of a new component',
                                  type: Type.String
                              }) componentType:AString,
                              @Param({
                                  title: 'componentName',
                                  tip: 'Name of a new component',
                                  type: Type.String
                              }) componentName:AString,
                              @Param({
                                  title: 'xOffsetInParent',
                                  tip: 'X offset in parent',
                                  type: Type.Number,
                                  optional: true
                              }) xOffsetInParent:ANumber,
                              @Param({
                                  title: 'yOffsetInParent',
                                  tip: 'Y offset in parent',
                                  type: Type.Number,
                                  optional: true
                              }) yOffsetInParent:ANumber,
                              @Param({
                                  title: 'width',
                                  tip: 'Width of a component',
                                  type: Type.Number,
                                  optional: true
                              }) width:ANumber,
                              @Param({
                                  title: 'height',
                                  tip: 'Height of a component',
                                  type: Type.Number,
                                  optional: true
                              }) height:ANumber):Promise<ABoolean | AError>
    {
        const result:boolean | Error = await api.getSurfaceContext().addComponent(
            pageName.value,
            parentComponentName.value,
            componentType.value,
            componentName.value,
            xOffsetInParent.value,
            yOffsetInParent.value,
            width.value,
            height.value
        );

        if( result instanceof Error )
            return new AError(result.message);

        return ABoolean.fromBoolean(result);
    }

    @Function({
        title: 'COPY_COMPONENT(srcPageName, srcComponentName, destPageName, destParentComponentName, newComponentName, keepBindings, xOffsetInParent, yOffsetInParent, width, height)',
        alias: 'COPY_COMPONENT',
        lazyOnly: true,
        tip: 'Copies an existing component',
        returns: {
            title: 'boolean',
            tip: 'Returns TRUE if a component has been copied. Otherwise returns FALSE.',
            type: Type.Boolean
        }
    })
    public async copyComponent(@Param({
                                   title: 'sheetAPI',
                                   tip: 'API',
                                   type: SheetType.SheetAPI
                               })api:ISheetAPI,
                               @Param({
                                   title: 'srcPageName',
                                   tip: 'Name of the source page',
                                   type: Type.String
                               }) srcPageName:AString,
                               @Param({
                                   title: 'srcComponentName',
                                   tip: 'Name of the source component',
                                   type: Type.String
                               }) srcComponentName:AString,
                               @Param({
                                   title: 'destPageName',
                                   tip: 'Name of the destination page',
                                   type: Type.String
                               }) destPageName:AString,
                               @Param({
                                   title: 'destParentComponentName',
                                   tip: 'Name of the destination parent component',
                                   type: Type.String,
                                   optional: true
                               }) destParentComponentName:AString,
                               @Param({
                                   title: 'newComponentName',
                                   tip: 'Name of a new component',
                                   type: Type.String
                               }) newComponentName:AString,
                               @Param({
                                   title: 'keepBindings',
                                   tip: 'Copies bindings as well. Default is TRUE',
                                   type: Type.Boolean,
                                   optional: true
                               }) keepBindings:ABoolean,
                               @Param({
                                   title: 'xOffsetInParent',
                                   tip: 'X offset in parent',
                                   type: Type.Number,
                                   optional: true
                               }) xOffsetInParent:ANumber,
                               @Param({
                                   title: 'yOffsetInParent',
                                   tip: 'Y offset in parent',
                                   type: Type.Number,
                                   optional: true
                               }) yOffsetInParent:ANumber,
                               @Param({
                                   title: 'width',
                                   tip: 'Width of a component',
                                   type: Type.Number,
                                   optional: true
                               }) width:ANumber,
                               @Param({
                                   title: 'height',
                                   tip: 'Height of a component',
                                   type: Type.Number,
                                   optional: true
                               }) height:ANumber):Promise<ABoolean | AError>
    {
        const result:boolean | Error = await api.getSurfaceContext().copyComponent(
            srcPageName.value,
            srcComponentName.value,
            destPageName.value,
            destParentComponentName.value,
            newComponentName.value,
            keepBindings.value,
            xOffsetInParent.value,
            yOffsetInParent.value,
            width.value,
            height.value
        );

        if( result instanceof Error )
            return new AError(result.message);

        return ABoolean.fromBoolean(result);
    }

    @Function({
        title: 'COPY_COMPONENTS(srcPageName, srcComponentName, destPageName, destParentComponentName, keepBindings, namesAndSizes)',
        alias: 'COPY_COMPONENTS',
        lazyOnly: true,
        tip: 'Copies an existing component multiple times',
        returns: {
            title: 'boolean',
            tip: 'Returns TRUE if a component has been copied. Otherwise returns FALSE.',
            type: Type.Boolean
        }
    })
    public async copyComponents(@Param({
                                    title: 'sheetAPI',
                                    tip: 'API',
                                    type: SheetType.SheetAPI
                                })api:ISheetAPI,
                                @Param({
                                    title: 'srcPageName',
                                    tip: 'Name of the source page',
                                    type: Type.String
                                }) srcPageName:AString,
                                @Param({
                                    title: 'srcComponentName',
                                    tip: 'Name of the source component',
                                    type: Type.String
                                }) srcComponentName:AString,
                                @Param({
                                    title: 'destPageName',
                                    tip: 'Name of the destination page',
                                    type: Type.String
                                }) destPageName:AString,
                                @Param({
                                    title: 'destParentComponentName',
                                    tip: 'Name of the destination parent component',
                                    type: Type.String,
                                    optional: true
                                }) destParentComponentName:AString,
                                @Param({
                                    title: 'keepBindings',
                                    tip: 'Copies bindings as well. Default is TRUE',
                                    type: Type.Boolean,
                                    optional: true
                                }) keepBindings:ABoolean,
                                @Param({
                                    title: 'namesAndSizes',
                                    tip: 'A frame that contains columns named "name", "x", "y", "width", "height"',
                                    type: Type.Frame
                                }) namesAndSizes:AFrame):Promise<ABoolean | AError>
    {
        const records:IFrameRecord[] = namesAndSizes.getRecords();

        if( records.length === 0 )
            return AError.fromString('Invalid namesAndSizes frame');

        for( const record of records )
        {
            const newComponentName = record.getCell('name', true) as AString;
            const xOffsetInParent = record.getCell('x', true) as ANumber;
            const yOffsetInParent = record.getCell('y', true) as ANumber;
            const width = record.getCell('width', true) as ANumber;
            const height = record.getCell('height', true) as ANumber;

            const result:boolean | Error = await api.getSurfaceContext().copyComponent(
                srcPageName.value,
                srcComponentName.value,
                destPageName.value,
                destParentComponentName.value,
                newComponentName.value,
                keepBindings.value,
                xOffsetInParent.value,
                yOffsetInParent.value,
                width.value,
                height.value
            );

            if( !result )
                return ABoolean.false();

            if( result instanceof Error )
                return new AError(result.message);
        }

        return ABoolean.true();
    }

    @Function({
        title: 'REMOVE_ALL_COMPONENTS(pageName)',
        alias: 'REMOVE_ALL_COMPONENTS',
        lazyOnly: true,
        tip: 'Removes all component on a given page',
        returns: {
            title: 'boolean',
            tip: 'Returns TRUE if components were removed. Otherwise returns FALSE.',
            type: Type.Boolean
        }
    })
    public removeAllComponents(@Param({
                                   title: 'sheetAPI',
                                   tip: 'API',
                                   type: SheetType.SheetAPI
                               })api:ISheetAPI,
                               @Param({
                                   title: 'pageName',
                                   tip: 'Name of the page',
                                   type: Type.String
                               }) pageName:AString):ABoolean | AError
    {
        const result:boolean | Error = api.getSurfaceContext().removeAllComponents(pageName.value);

        if( result instanceof Error )
            return new AError(result.message);

        return ABoolean.fromBoolean(result);
    }

    @Function({
        title: 'REMOVE_COMPONENTS_OF(pageName, parentComponentName)',
        alias: 'REMOVE_COMPONENTS_OF',
        lazyOnly: true,
        tip: 'Removes components',
        returns: {
            title: 'boolean',
            tip: 'Returns TRUE if components were removed. Otherwise returns FALSE.',
            type: Type.Boolean
        }
    })
    public removeComponents(@Param({
                                title: 'sheetAPI',
                                tip: 'API',
                                type: SheetType.SheetAPI
                            })api:ISheetAPI,
                            @Param({
                                title: 'pageName',
                                tip: 'Name of the page',
                                type: Type.String
                            }) pageName:AString,
                            @Param({
                                title: 'parentComponentName',
                                tip: 'Name of the parent component',
                                type: Type.String
                            }) parentComponentName:AString):ABoolean | AError
    {
        const result:boolean | Error = api.getSurfaceContext().removeComponents(
            pageName.value,
            parentComponentName.value
        );

        if( result instanceof Error )
            return new AError(result.message);

        return ABoolean.fromBoolean(result);
    }

    @Function({
        title: 'REMOVE_COMPONENT(pageName, componentName)',
        alias: 'REMOVE_COMPONENT',
        lazyOnly: true,
        tip: 'Removes component',
        returns: {
            title: 'boolean',
            tip: 'Returns TRUE if component was removed. Otherwise returns FALSE.',
            type: Type.Boolean
        }
    })
    public removeComponent(@Param({
                               title: 'sheetAPI',
                               tip: 'API',
                               type: SheetType.SheetAPI
                           })api:ISheetAPI,
                           @Param({
                               title: 'pageName',
                               tip: 'Name of the page',
                               type: Type.String
                           }) pageName:AString,
                           @Param({
                               title: 'componentName',
                               tip: 'Name of the component',
                               type: Type.String
                           }) componentName:AString):ABoolean | AError
    {
        const result:boolean | Error = api.getSurfaceContext().removeComponent(
            pageName.value,
            componentName.value
        );

        if( result instanceof Error )
            return new AError(result.message);

        return ABoolean.fromBoolean(result);
    }

    @Function({
        title: 'SET_BINDING(pageName, componentName, propOrEventName, sheetName, cellRef)',
        alias: 'SET_BINDING',
        lazyOnly: true,
        tip: 'Sets component binding',
        returns: {
            title: 'boolean',
            tip: 'Returns TRUE if binding has been set. Otherwise returns FALSE.',
            type: Type.Boolean
        }
    })
    public setBinding(@Param({
                          title: 'sheetAPI',
                          tip: 'API',
                          type: SheetType.SheetAPI
                      })api:ISheetAPI,
                      @Param({
                          title: 'pageName',
                          tip: 'Name of the page',
                          type: Type.String
                      }) pageName:AString,
                      @Param({
                          title: 'componentName',
                          tip: 'Name of the component',
                          type: Type.String
                      }) componentName:AString,
                      @Param({
                          title: 'propOrEventName',
                          tip: 'Name of the property or event',
                          type: Type.String
                      }) propOrEventName:AString,
                      @Param({
                          title: 'sheetName',
                          tip: 'Name of the sheet',
                          type: Type.String
                      }) sheetName:AString,
                      @Param({
                          title: 'cellLocation',
                          tip: 'Cell location',
                          type: SheetType.SheetLocation
                      }) cellLocation:ISheetLocation,
                      @Param({
                          title: 'pathInFrame',
                          tip: 'Path (jsonpath) in frame (e.g. column1[0].column2[0])',
                          type: Type.String,
                          optional: true
                      }) pathInFrame:AString,
                      @Param({
                          title: 'fixedDirection',
                          tip: 'Binding fixed direction (Available options: fixed, down, right. Not fixed by default.)',
                          type: Type.String,
                          optional: true
                      }) fixedDirection:AString,
                      @Param({
                          title: 'useFormat',
                          tip: 'Whether use formatting or not, boolean (Available options: true, false)',
                          type: Type.Boolean,
                          optional: true
                      }) useFormat:ABoolean):ABoolean | AError
    {
        const result:boolean | Error = api.getSurfaceContext().setBinding(
            pageName.value,
            componentName.value,
            propOrEventName.value,
            sheetName.value,
            cellLocation,
            pathInFrame ? pathInFrame.value : null,
            fixedDirection ? fixedDirection.value : null,
            useFormat ? useFormat.value : null,
            null,
            null
        );

        if( result instanceof Error )
            return new AError(result.message);

        return ABoolean.fromBoolean(result);
    }

    @Function({
        title: 'SET_RANGE_BINDING(pageName, componentName, propName, sheetName, cellRef)',
        alias: 'SET_RANGE_BINDING',
        lazyOnly: true,
        tip: 'Sets component range binding',
        returns: {
            title: 'boolean',
            tip: 'Returns TRUE if binding has been set. Otherwise returns FALSE.',
            type: Type.Boolean
        }
    })
    public setRangeBinding(@Param({
                               title: 'sheetAPI',
                               tip: 'API',
                               type: SheetType.SheetAPI
                           })api:ISheetAPI,
                           @Param({
                               title: 'pageName',
                               tip: 'Name of the page',
                               type: Type.String
                           }) pageName:AString,
                           @Param({
                               title: 'componentName',
                               tip: 'Name of the component',
                               type: Type.String
                           }) componentName:AString,
                           @Param({
                               title: 'propName',
                               tip: 'Name of the property. (e.g. repeater.values).',
                               type: SheetType.AnyIValue
                           }) propName:AString,
                           @Param({
                               title: 'sheetName',
                               tip: 'Name of the sheet',
                               type: Type.String
                           }) sheetName:AString,
                           @Param({
                               title: 'cellLocation',
                               tip: 'Cell location to start range binding from',
                               type: SheetType.SheetLocation
                           }) cellLocation:ISheetLocation,
                           @Param({
                               title: 'pathInFrame',
                               tip: 'Path (jsonpath) in frame to start range binding from (e.g. column1[0].column2[0])',
                               type: Type.String,
                               optional: true
                           }) pathInFrame:AString,
                           @Param({
                               title: 'direction',
                               tip: 'Binding direction (Available options: down (default), right)',
                               type: Type.String,
                               optional: true
                           }) direction:AString,
                           @Param({
                               title: 'itemsCount',
                               tip: 'Number of rows/columns to bind. Auto mode if omitted',
                               type: Type.Number,
                               optional: true
                           }) itemsCount:ANumber,
                           @Param({
                               title: 'fixedDirection',
                               tip: 'Binding fixed direction (Available options: fixed, down, right. Not fixed by default.)',
                               type: Type.String,
                               optional: true
                           }) fixedDirection:AString,
                           @Param({
                               title: 'useFormat',
                               tip: 'Whether use formatting or not, boolean (Available options: true, false)',
                               type: Type.Boolean,
                               optional: true
                           }) useFormat:ABoolean):ABoolean | AError
    {
        const result:boolean | Error = api.getSurfaceContext().setBinding(
            pageName.value,
            componentName.value,
            propName.value,
            sheetName.value,
            cellLocation,
            pathInFrame ? pathInFrame.value : null,
            fixedDirection ? fixedDirection.value : null,
            useFormat ? useFormat.value : null,
            direction ? direction.value : null,
            itemsCount ? itemsCount.value : null
        );

        if( result instanceof Error )
            return new AError(result.message);

        return ABoolean.fromBoolean(result);
    }
}
